import React, { FC, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';

import FilterableTable from '../../../components/FilterableTable/FilterableTable';
import Loading from '../../../components/Loading/Loading';
import Page from '../../../components/Page/Page';
import { TabState } from '../../../components/StatefulTabs/StatefulTabs';
import { useReferenceData, useUserProfile } from '../../../hooks/reduxHooks';
import { getPhysicalOrders, PhysicalOrderIndexData } from '../../../services/ordersService';
import { mapToDisplayedStatus } from '../../../utils/orderUtils';
import { pullAllResults } from '../../../utils/serviceUtils';
import { getDataFormat, getFilterProperties, getPermissions } from '../PhysicalOrdersManagement.helpers';
import { getUsersInBulk } from '../../../services';

interface OrdersTableTabProps {
    eventKey: string;
    activePlatforms?: boolean;
}
const OrdersTableTab:FC<OrdersTableTabProps> = ({ eventKey, activePlatforms }) => {
	const userProfile = useUserProfile();
	const platforms = useReferenceData('platforms');
	const show = useContext(TabState)?.tab === eventKey;
	const { canViewAllOrders, canViewCreditHoldOrders } = getPermissions(userProfile);
	const ordersType = activePlatforms ? 'active' : 'legacy';
	const ordersQuery = useQuery(
		['pullAllResults:getPhysicalOrders', 8000, { platform_status: ordersType }],
		() => pullAllResults<PhysicalOrderIndexData>(getPhysicalOrders, 8000, {
			platform_status: ordersType,
		}),
		{ enabled: show },
	);
	const orders = (ordersQuery.isSuccess && ordersQuery.data) || undefined;
	const ordersWithDisplayStatus = orders?.map((order) => {
		return {...order, displayStatus: mapToDisplayedStatus(order, userProfile)};
	});

	const submittedByNdidList = useMemo(() => {
		if (
			canViewCreditHoldOrders &&
			Array.isArray(ordersQuery.data)
		) {
			const ndids = ordersQuery.data
				.map(order => order?.submitted_by_ndid)
				.filter((v): v is string => typeof v === 'string');
			return Array.from(new Set(ndids));
		}
		return [];
	}, [canViewCreditHoldOrders, ordersQuery.data]);

	const usersQuery = useQuery(
		['getUsersInBulk', ...submittedByNdidList],
		() =>
			getUsersInBulk(
				submittedByNdidList,
				['email_address']
			),
		{ enabled: !!submittedByNdidList.length,
			staleTime: Infinity },
	);
	const users = usersQuery.data?.data;
	const dataFormat = useMemo(() => getDataFormat(userProfile, users), [userProfile, users]);

	const filterProperties = useMemo(
		() => 
			ordersWithDisplayStatus && ordersWithDisplayStatus.length > 1 && !canViewCreditHoldOrders
				? userProfile && platforms && getFilterProperties(userProfile, platforms, activePlatforms)
				: undefined,
		[userProfile, platforms, activePlatforms, ordersWithDisplayStatus],
	);
	const searchableFields = 
		canViewAllOrders
			? ['noa_po', 'publisher_po', 'publisher', 'req_number', 'noa_sales_order_number']
			: canViewCreditHoldOrders 
				? ['publisher_po', 'publisher', 'noa_sales_order_number'] 
				: ['publisher_po'] 
	;
	const searchableFieldPlaceHolder = canViewAllOrders || canViewCreditHoldOrders
		? 'Search Within...'
		: 'Search by Publisher PO...';

	return <Page.ContentContainer>{ ordersQuery.isLoading || (canViewCreditHoldOrders && usersQuery.isLoading) ? (
        <Loading />
	) : (
        <FilterableTable
            dataFormat={dataFormat}
            data={ordersWithDisplayStatus}
            filterProperties={filterProperties}
            searchableFields={searchableFields}
            searchableFieldPlaceHolder={searchableFieldPlaceHolder}
            defaultSorted={[
                {
                    id: 'submission_date',
                    desc: true,
                },
            ]}
            noBorder
            retainPageState
            stateOnTab={eventKey}
        />)
        }</Page.ContentContainer>;
};

export default OrdersTableTab;
